import { useCallback, useEffect, useRef, useState } from 'react';
import type { ClientOut, ServerIn } from './protocol';

export type SignalingStatus =
  | { kind: 'idle' }
  | { kind: 'connecting' }
  | { kind: 'open' }
  | { kind: 'closed'; code: number; reason: string }
  | { kind: 'error'; message: string };

export interface UseSignalingOpts {
  url: string;
  onMessage: (msg: ServerIn) => void;
  /** If true, the hook will not auto-reconnect on close (default false for one-shot sessions). */
  noReconnect?: boolean;
}

export interface UseSignalingApi {
  status: SignalingStatus;
  send: (msg: ClientOut) => boolean;
  close: (code?: number, reason?: string) => void;
}

const MAX_BACKOFF_MS = 15_000;

/**
 * Minimal WSS client for the signaling channel.
 *
 * Lifecycle: `idle` → `connecting` → `open` → (`closed` | `error`). Manual reconnects are intentional — the
 * session page decides whether to retry based on the failure reason (bad password vs network glitch).
 */
export function useSignaling({ url, onMessage, noReconnect }: UseSignalingOpts): UseSignalingApi {
  const wsRef = useRef<WebSocket | null>(null);
  const onMessageRef = useRef(onMessage);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef<number | null>(null);
  const [status, setStatus] = useState<SignalingStatus>({ kind: 'idle' });

  // Keep the latest callback without re-creating the socket on every render.
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  const connect = useCallback(() => {
    if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
      return;
    }
    setStatus({ kind: 'connecting' });
    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch (err) {
      setStatus({ kind: 'error', message: (err as Error).message });
      return;
    }
    wsRef.current = ws;
    ws.binaryType = 'arraybuffer';

    ws.onopen = () => {
      reconnectAttemptRef.current = 0;
      setStatus({ kind: 'open' });
    };
    ws.onmessage = (ev) => {
      try {
        const raw = typeof ev.data === 'string' ? ev.data : new TextDecoder().decode(ev.data as ArrayBuffer);
        const msg = JSON.parse(raw) as ServerIn;
        onMessageRef.current(msg);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('signaling: bad message', err);
      }
    };
    ws.onerror = () => {
      // The browser does not give a useful message on `error`; the subsequent `close` carries the code.
      // Don't update status here — wait for close.
    };
    ws.onclose = (ev) => {
      wsRef.current = null;
      setStatus({ kind: 'closed', code: ev.code, reason: ev.reason });
      if (!noReconnect && ev.code !== 4003 && ev.code !== 4029 && ev.code !== 4004) {
        // Retry on transient closes; do NOT retry on bad password (4003), rate-limited (4029) or host offline (4004).
        const attempt = ++reconnectAttemptRef.current;
        const backoff = Math.min(500 * 2 ** attempt, MAX_BACKOFF_MS);
        reconnectTimerRef.current = window.setTimeout(connect, backoff);
      }
    };
  }, [url, noReconnect]);

  useEffect(() => {
    connect();
    return () => {
      if (reconnectTimerRef.current) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (wsRef.current) {
        try {
          wsRef.current.close(1000, 'unmount');
        } catch {
          /* ignore */
        }
        wsRef.current = null;
      }
    };
  }, [connect]);

  const send = useCallback((msg: ClientOut): boolean => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    try {
      ws.send(JSON.stringify(msg));
      return true;
    } catch {
      return false;
    }
  }, []);

  const close = useCallback((code = 1000, reason = 'client close'): void => {
    if (reconnectTimerRef.current) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    const ws = wsRef.current;
    wsRef.current = null;
    if (ws) {
      try {
        ws.close(code, reason);
      } catch {
        /* ignore */
      }
    }
  }, []);

  return { status, send, close };
}
